import type { UnknownAction } from "@reduxjs/toolkit";
import { v4 } from "uuid";

import type { AppDispatch, RootState } from "@src/store/rootState";
import { closeDialog, openDialog } from "@src/store/slices/global";
import {
  buildFinished,
  buildStarted,
  stepsAdded,
  stepUpdated,
} from "@src/store/slices/testBuilds";
import type { TestBuildStartedBy } from "@src/store/slices/testBuilds/types";
import { waitForAction } from "@src/store/slices/testBuilds/waitForAction";

/** How long the build dialog stays open at least, so a quick build does not flash it. */
export const BUILD_DIALOG_MIN_MS = 1000;

/** The running builds' cancel switches, by build id. Kept out of the store: they are not data. */
const cancels = new Map<string, AbortController>();

/**
 * Stops a running build. A wait stops at once; a request already sent runs
 * to its end, and no step runs after it. Whatever it made so far stays.
 */
export const cancelTestBuild = (buildId: string) =>
  cancels.get(buildId)?.abort();

export interface BuildStepContext {
  dispatch: AppDispatch;
  getState: () => RootState;
  /** Says what the running step is waiting for. */
  setDetail: (detail: string) => void;
  /** Runs these steps right after the running one. */
  addSteps: (steps: BuildStepDefinition[]) => void;
  /** Aborts when the build is cancelled. */
  signal: AbortSignal;
  /** Waits for a dispatched action, such as a websocket message. Ends early on cancel. */
  waitFor: <A extends UnknownAction>(
    predicate: (action: UnknownAction) => action is A,
    timeoutMs: number
  ) => ReturnType<typeof waitForAction<A>>;
}

export interface BuildStepDefinition {
  id: string;
  label: string;
  /** "stop" ends the build here, as done: what it found needs no more steps. */
  run: (ctx: BuildStepContext) => Promise<void | "stop">;
}

/**
 * Runs a build's steps in order and records each one in the `testBuilds`
 * slice. The build dialog shows them, so a surface only starts the build.
 * A step that throws fails itself and the build, and the error reaches the
 * caller. Steps share what they find through the caller's own variables.
 * Resolves with how the build ended; a cancelled one opens nothing.
 */
export const runTestBuild =
  ({
    startedBy,
    name = null,
    typeLabel = null,
    steps,
  }: {
    startedBy: TestBuildStartedBy;
    name?: string | null;
    typeLabel?: string | null;
    steps: BuildStepDefinition[];
  }) =>
  async (
    dispatch: AppDispatch,
    getState: () => RootState
  ): Promise<"done" | "cancelled"> => {
    const buildId = v4();
    const cancel = new AbortController();
    cancels.set(buildId, cancel);
    const cancelled = () => {
      cancels.delete(buildId);
      dispatch(buildFinished({ buildId, status: "cancelled" }));
      dispatch(closeDialog("testBuild"));
      return "cancelled" as const;
    };
    // Every build ends by opening the test, which its starter does once the
    // build is done, so even an instant build shows more than one step.
    const queue = [
      ...steps,
      { id: "open", label: "Opening the test", run: async () => undefined },
    ];
    dispatch(
      buildStarted({
        id: buildId,
        startedBy,
        name,
        typeLabel,
        steps: queue.map(({ id, label }) => ({ id, label })),
      })
    );
    dispatch(openDialog({ dialog: "testBuild", props: { buildId } }));
    const openedAt = Date.now();
    for (let at = 0; at < queue.length; at++) {
      if (cancel.signal.aborted) {
        return cancelled();
      }
      const step = queue[at];
      const update = (change: {
        status?: "running" | "done" | "failed";
        detail?: string;
      }) => dispatch(stepUpdated({ buildId, stepId: step.id, ...change }));
      update({ status: "running" });
      let result: void | "stop";
      try {
        result = await step.run({
          dispatch,
          getState,
          setDetail: (detail) => update({ detail }),
          addSteps: (added) => {
            queue.splice(at + 1, 0, ...added);
            dispatch(
              stepsAdded({
                buildId,
                afterStepId: step.id,
                steps: added.map(({ id, label }) => ({ id, label })),
              })
            );
          },
          signal: cancel.signal,
          waitFor: <A extends UnknownAction>(
            predicate: (action: UnknownAction) => action is A,
            timeoutMs: number
          ) => waitForAction(dispatch, predicate, timeoutMs, cancel.signal),
        });
      } catch (error) {
        cancels.delete(buildId);
        update({ status: "failed" });
        dispatch(buildFinished({ buildId, status: "failed" }));
        throw error;
      }
      if (cancel.signal.aborted) {
        return cancelled();
      }
      update({ status: "done" });
      if (result === "stop") {
        break;
      }
    }
    cancels.delete(buildId);
    dispatch(buildFinished({ buildId, status: "done" }));
    const shown = Date.now() - openedAt;
    if (shown < BUILD_DIALOG_MIN_MS) {
      await new Promise((resolve) =>
        setTimeout(resolve, BUILD_DIALOG_MIN_MS - shown)
      );
    }
    dispatch(closeDialog("testBuild"));
    return "done";
  };
